import React, { useEffect, useMemo, useState } from 'react';
import {
  LogIn, LogOut, ShieldAlert, Plus, Pencil, Trash2, Check, X, Download, FileDown,
  KeyRound, Settings2, Search, ChevronDown, Flag, Monitor, MapPin, Filter, RefreshCw,
} from 'lucide-react';
import api from '@/services/api';

/**
 * Audit log: everything people do in the system, who did it, from where and
 * what changed. Read-only. Used on Admin › Audit Logs and the GMD dashboard
 * (embedded). Events come from GET /audit-logs/events?days=N.
 */
const TYPES = {
  signin: 'Sign-in', signin_failed: 'Sign-in', signout: 'Sign-in',
  create: 'Changes', update: 'Changes', delete: 'Changes',
  approve: 'Approvals', reject: 'Approvals',
  export: 'Data out', download: 'Data out',
  access: 'Access', settings: 'Access',
};
const GROUPS = ['Sign-in', 'Changes', 'Approvals', 'Data out', 'Access'];
const MODULES = ['Requests', 'Finance', 'HR', 'Inventory', 'Projects', 'Documents', 'Communication', 'Self Service', 'Users', 'Admin', 'Sign-in', 'System'];
const ICON = { signin: LogIn, signin_failed: ShieldAlert, signout: LogOut, create: Plus, update: Pencil, delete: Trash2, approve: Check, reject: X, export: FileDown, download: Download, access: KeyRound, settings: Settings2 };
const RANGES = [['today', 'Today', 1], ['7', '7 days', 7], ['30', '30 days', 30], ['all', 'All', 3650]];
const NOBODY = ['Unknown', 'Someone'];

const time = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(' ', '');
const dayLabel = (iso) => {
  const d = new Date(iso).toDateString();
  if (d === new Date().toDateString()) return 'Today';
  if (d === new Date(Date.now() - 864e5).toDateString()) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
};
const sentence = (e) => `${e.actor.name} ${e.verb.replace('{x}', e.target?.label || '')}`;
// The same sentence with the names in bold
const Sentence = ({ e }) => {
  const [a, b] = e.verb.split('{x}');
  return (
    <>
      <span className="font-medium text-gray-900">{e.actor.name}</span> {a}
      {b !== undefined && <span className="font-medium text-gray-900">{e.target?.label}</span>}
      {b}
    </>
  );
};
const initials = (n) => n.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
const CARD = 'rounded-xl border border-gray-200 bg-white';
const SELECT = 'h-9 rounded-lg border border-gray-300 bg-white pl-3 pr-8 text-[13px] text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

const AuditLogs = ({ embedded = false }) => {
  const [range, setRange] = useState('7');
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const [person, setPerson] = useState('');
  const [mod, setMod] = useState('');
  const [group, setGroup] = useState('');
  const [risky, setRisky] = useState(false);
  const [open, setOpen] = useState(null);
  const [limit, setLimit] = useState(60);

  const days = RANGES.find((r) => r[0] === range)?.[2] || 7;
  const load = async () => {
    try {
      setLoading(true);
      setFailed(false);
      const { data } = await api.get('/audit-logs/events', { params: { days } });
      setAll(data?.data?.events || []);
    } catch {
      setFailed(true);
      setAll([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [days]);
  useEffect(() => { setLimit(60); }, [range, person, mod, group, risky, q]);

  const inRange = useMemo(
    () => (range === 'today' ? all.filter((e) => new Date(e.at).toDateString() === new Date().toDateString()) : all),
    [all, range],
  );
  const term = q.trim().toLowerCase();
  const rows = inRange.filter((e) =>
    (!person || e.actor.name === person) &&
    (!mod || e.module === mod) &&
    (!group || TYPES[e.type] === group) &&
    (!risky || e.risky) &&
    (!term || `${sentence(e)} ${e.module} ${e.device} ${e.ip}`.toLowerCase().includes(term)));

  const people = useMemo(() => {
    const m = {};
    inRange.forEach((e) => {
      if (!NOBODY.includes(e.actor.name)) m[e.actor.name] = (m[e.actor.name] || 0) + (e.type === 'signin' ? 0 : 1);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [inRange]);
  const toCheck = inRange.filter((e) => e.risky);
  const stats = [
    ['Actions', inRange.filter((e) => !['signin', 'signout', 'signin_failed'].includes(e.type)).length, 'Changes and approvals', false],
    ['People active', new Set(inRange.filter((e) => !NOBODY.includes(e.actor.name)).map((e) => e.actor.name)).size, 'Did at least one thing', false],
    ['Failed sign-ins', inRange.filter((e) => e.type === 'signin_failed').length, 'Wrong password or code', true],
    ['Data taken out', inRange.filter((e) => ['export', 'download'].includes(e.type)).length, 'Exports and downloads', false],
  ];

  const exportCsv = () => {
    const esc = (x) => `"${String(x ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map((e) => [
      new Date(e.at).toLocaleString('en-GB'), e.actor.name, e.actor.role, sentence(e), e.module,
      (e.changes || []).map((c) => `${c.field}: ${c.from} → ${c.to}`).join('; '), e.ip, e.device,
    ].map(esc).join(','));
    const head = ['When', 'Who', 'Role', 'What', 'Module', 'Changes', 'IP', 'Device'].join(',');
    const url = URL.createObjectURL(new Blob([[head, ...lines].join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  let lastDay = '';
  return (
    <div className={embedded ? '' : 'mx-auto w-full max-w-[1600px] pb-16'}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={`${embedded ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>Audit log</h1>
          <p className="mt-1 text-sm text-gray-500">
            Everything people do in the system: who, what, when and from where. Nobody can change or delete it.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button type="button" onClick={exportCsv} disabled={!rows.length} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map(([label, value, sub, warn]) => (
          <div key={label} className={`${CARD} p-4`}>
            <p className="text-[12px] text-gray-500">{label}</p>
            <p className={`text-[22px] font-semibold tabular-nums ${warn && value ? 'text-red-700' : 'text-gray-900'}`}>{value.toLocaleString()}</p>
            <p className="text-[11.5px] text-gray-400">{sub}</p>
          </div>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5">
          {RANGES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setRange(k)} className={`h-8 rounded-md px-2.5 text-[12.5px] font-medium ${range === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>{l}</button>
          ))}
        </div>
        <select value={person} onChange={(e) => setPerson(e.target.value)} className={SELECT}>
          <option value="">Everyone</option>
          {people.map(([n]) => <option key={n}>{n}</option>)}
        </select>
        <select value={mod} onChange={(e) => setMod(e.target.value)} className={SELECT}>
          <option value="">Any module</option>
          {MODULES.map((m) => <option key={m}>{m}</option>)}
        </select>
        <select value={group} onChange={(e) => setGroup(e.target.value)} className={SELECT}>
          <option value="">Any action</option>
          {GROUPS.map((g) => <option key={g}>{g}</option>)}
        </select>
        <button type="button" onClick={() => setRisky(!risky)} className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-medium ${risky ? 'bg-primary-700 text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
          <Flag size={13} />To check
          {toCheck.length > 0 && <span className={`rounded px-1 text-[11px] ${risky ? 'bg-white/20' : 'bg-red-50 text-red-700'}`}>{toCheck.length}</span>}
        </button>
        <label className="flex h-9 w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 sm:ml-auto sm:w-[260px]">
          <Search size={14} className="text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names, records, IPs" className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] focus:outline-none focus:ring-0" />
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_290px]">
        <section className={`${CARD} overflow-hidden`}>
          {loading && !all.length ? (
            <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" /></div>
          ) : failed ? (
            <p className="px-5 py-16 text-center text-[13px] text-red-700">Couldn't load the audit log. Try Refresh.</p>
          ) : !rows.length ? (
            <p className="px-5 py-16 text-center text-[13px] text-gray-500">Nothing matches. Try a longer period or fewer filters.</p>
          ) : (
            rows.slice(0, limit).map((e) => {
              const Icon = ICON[e.type] || Pencil;
              const day = dayLabel(e.at);
              const header = day !== lastDay;
              lastDay = day;
              const isOpen = open === e.id;
              return (
                <React.Fragment key={e.id}>
                  {header && <div className="border-b border-gray-100 bg-gray-50 px-5 py-2 text-[12px] font-semibold text-gray-600">{day}</div>}
                  <div className={`border-b border-gray-100 last:border-0 ${isOpen ? 'bg-gray-50' : ''}`}>
                    <button type="button" onClick={() => setOpen(isOpen ? null : e.id)} className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-gray-50">
                      <span className="w-[56px] flex-shrink-0 text-[12px] tabular-nums text-gray-400">{time(e.at)}</span>
                      <span className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg ${e.risky ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'}`}><Icon size={13} /></span>
                      <span className="min-w-0 flex-1 truncate text-[13px] text-gray-600">
                        <Sentence e={e} />
                        {e.changes?.length > 0 && <span className="text-gray-400"> · {e.changes.length === 1 ? e.changes[0].field : `${e.changes.length} fields`}</span>}
                      </span>
                      <span className="hidden w-[110px] flex-shrink-0 truncate text-[12px] text-gray-500 md:block">{e.module}</span>
                      <span className="hidden w-[150px] flex-shrink-0 truncate text-right text-[12px] text-gray-400 lg:block">{e.device}</span>
                      <ChevronDown size={14} className={`flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="space-y-3 px-5 pb-4 sm:pl-[136px]">
                        {e.changes?.length > 0 && (
                          <table className="w-full max-w-[560px] overflow-hidden rounded-lg border border-gray-200 bg-white text-[12.5px]">
                            <thead><tr className="text-left text-[11px] text-gray-400"><th className="px-3 py-1.5 font-medium">Field</th><th className="px-3 py-1.5 font-medium">Before</th><th className="px-3 py-1.5 font-medium">After</th></tr></thead>
                            <tbody>{e.changes.map((c) => <tr key={c.field} className="border-t border-gray-100"><td className="px-3 py-1.5 text-gray-500">{c.field}</td><td className="px-3 py-1.5 text-gray-400 line-through">{c.from}</td><td className="px-3 py-1.5 font-medium text-gray-900">{c.to}</td></tr>)}</tbody>
                          </table>
                        )}
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-gray-500">
                          <span>{new Date(e.at).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })}</span>
                          {e.actor.role && <span>{e.actor.role}</span>}
                          {e.device && <span className="inline-flex items-center gap-1"><Monitor size={12} />{e.device}</span>}
                          {e.ip && <span className="inline-flex items-center gap-1"><MapPin size={12} />{e.ip}</span>}
                        </div>
                        {!NOBODY.includes(e.actor.name) && person !== e.actor.name && (
                          <button type="button" onClick={() => setPerson(e.actor.name)} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-gray-500 hover:text-gray-900">
                            <Filter size={12} />Everything by {e.actor.name.split(' ')[0]}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
          {rows.length > limit && (
            <button type="button" onClick={() => setLimit((l) => l + 100)} className="w-full border-t border-gray-100 py-3 text-[13px] font-medium text-primary-700 hover:bg-gray-50">
              Show more ({(rows.length - limit).toLocaleString()} left)
            </button>
          )}
        </section>

        <div className="space-y-4">
          <section className={`${CARD} overflow-hidden`}>
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
              <Flag size={14} className="text-red-700" />
              <p className="flex-1 text-[13.5px] font-semibold text-gray-900">Worth checking</p>
              <span className="text-[12px] text-gray-400">{toCheck.length}</span>
            </div>
            {toCheck.slice(0, 6).map((e) => {
              const Icon = ICON[e.type] || Pencil;
              return (
                <button key={e.id} type="button" onClick={() => { setRisky(true); setOpen(e.id); }} className="flex w-full items-start gap-2.5 border-b border-gray-100 px-4 py-2.5 text-left last:border-0 hover:bg-gray-50">
                  <Icon size={13} className="mt-0.5 flex-shrink-0 text-red-700" />
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-[12.5px] text-gray-900">{sentence(e)}</span>
                    <span className="text-[11.5px] text-gray-400">{dayLabel(e.at)}, {time(e.at)}</span>
                  </span>
                </button>
              );
            })}
            {!toCheck.length && <p className="px-4 py-6 text-center text-[12.5px] text-gray-500">Nothing unusual in this period.</p>}
            <p className="border-t border-gray-100 px-4 py-2.5 text-[11.5px] text-gray-400">Failed sign-ins, refused access, other people's passwords and roles changed, deleted records, exports and errors.</p>
          </section>
          <section className={`${CARD} overflow-hidden`}>
            <div className="border-b border-gray-100 px-4 py-3"><p className="text-[13.5px] font-semibold text-gray-900">Most active</p></div>
            {people.slice(0, 8).map(([n, c]) => (
              <button key={n} type="button" onClick={() => setPerson(person === n ? '' : n)} className={`flex w-full items-center gap-2.5 border-b border-gray-100 px-4 py-2 text-left last:border-0 hover:bg-gray-50 ${person === n ? 'bg-primary-50' : ''}`}>
                <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-800">{initials(n)}</span>
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-900">{n}</span>
                <span className="text-[12px] tabular-nums text-gray-500">{c}</span>
              </button>
            ))}
            {!people.length && <p className="px-4 py-6 text-center text-[12.5px] text-gray-500">No one yet.</p>}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
